<template>
    <article class="transaction-card">
        <span class="direction-icon" :class="directionClass">
            <img src="/assets/images/wallets/activity-receive.svg" alt="" width="23" height="23">
        </span>

        <div class="transaction-main">
            <div class="transaction-meta">
                <div class="meta-item">
                    <div class="meta-label">{{ $t('userWallet.history.date') }}</div>
                    <p class="meta-value">{{ formatDateTime(item.occurred_at) }}</p>
                </div>
                <div class="meta-item">
                    <div class="meta-label">{{ $t('userWallet.history.amount') }} ({{ currencyLabel }})</div>
                    <p class="meta-value">{{ formatAmount(item.amount) }} </p>
                </div>
                <div class="meta-item">
                    <div class="meta-label">{{ $t('userWallet.history.type') }}</div>
                    <p class="meta-value">{{ $t('userWallet.history.typeLabel.' + typeLabelKey) }}</p>
                </div>
                <div class="meta-item">
                    <div class="meta-label">{{ $t('userWallet.history.status') }}</div>
                    <p class="meta-value">
                        <span class="status-value" :class="'is-' + item.activity_status">
                            <img v-if="item.activity_status === 'success'" src="/assets/images/wallets/status-success.svg" alt="" width="18" height="18">
                            <img v-else-if="item.activity_status === 'failed'" src="/assets/images/wallets/status-failed.svg" alt="" width="18" height="18">
                            <img v-else src="/assets/images/wallets/status-pending.svg" alt="" width="18" height="18">
                            {{ $t('userWallet.history.statusLabel.' + item.activity_status) }}
                        </span>
                    </p>
                </div>
            </div>

            <dl v-if="addressLabel" class="address-block">
                <dt>{{ addressLabel }}</dt>
                <dd>{{ addressValue }}</dd>
            </dl>

            <dl v-if="executionContentLabel" class="address-block">
                <dt>{{ $t('userWallet.history.executionContent') }}</dt>
                <dd>{{ executionContentLabel }}</dd>
            </dl>

            <div v-if="item.transaction_hash || showEstimatedGas" class="chain-metadata" aria-label="メタデータ">
                <div v-if="showEstimatedGas" class="chain-metadata__item">
                    <span>{{ $t('userWallet.history.estimatedGasFee') }}</span>
                    <strong>{{ formatAmount(item.estimated_gas_eth) }} ETH</strong>
                </div>
                <div v-else-if="item.transaction_fee && (direction === 'out' || item.purpose === PURPOSE_TYPES.UNSTAKE)" class="chain-metadata__item">
                    <span>{{ $t('userWallet.history.gasFee') }}</span>
                    <strong>{{ formatAmount(item.transaction_fee) }} ETH</strong>
                </div>

                <a v-if="item.block_number && explorerBlockUrl" :href="explorerBlockUrl" target="_blank" rel="noopener noreferrer">
                    <span>{{ $t('userWallet.history.blockNumber') }}</span>
                    <strong>{{ item.block_number }}<img src="/assets/images/wallets/external-link.svg" alt="" width="14" height="14"></strong>
                </a>

                <template v-if="item.transaction_hash">
                    <a v-if="explorerTxUrl" :href="explorerTxUrl" target="_blank" rel="noopener noreferrer">
                        <span>{{ $t('userWallet.history.txHash') }}</span>
                        <strong>{{ item.transaction_hash }}<img src="/assets/images/wallets/external-link.svg" alt="" width="14" height="14"></strong>
                    </a>

                    <dl v-else class="address-block">
                        <dt>{{ $t('userWallet.history.txHash') }}</dt>
                        <dd>{{ item.transaction_hash }}</dd>
                    </dl>
                </template>
            </div>
        </div>
    </article>
</template>

<script>
    import walletFormatMixin from '../../../utils/walletFormatMixin';
    import config from '../../../../config';
    import {CURRENCIES, ACTIVITY_TYPES, PURPOSE_TYPES} from '../constants/currencies.js';

    export default {
        name: 'WalletActivityItem',

        mixins: [walletFormatMixin],

        props: {
            item: {type: Object, required: true},
            currencySymbol: {type: String, required: true},
        },

        computed: {
            PURPOSE_TYPES() {
                return PURPOSE_TYPES;
            },

            currencyLabel() {
                const currency = CURRENCIES[this.currencySymbol];
                return currency ? currency.unitLabel : this.currencySymbol;
            },

            typeLabelKey() {
                return this.item.purpose === PURPOSE_TYPES.STAKE ? this.item.purpose : this.item.activity_type;
            },

            executionContentLabel() {
                if (this.item.purpose !== PURPOSE_TYPES.UNSTAKE) {
                    return null;
                }
                return this.$t('userWallet.history.executionContentLabel.' + this.item.purpose);
            },

            direction() {
                return this.item.activity_type === ACTIVITY_TYPES.RECEIVE ? 'in' : 'out';
            },

            directionClass() {
                return 'is-' + this.direction;
            },

            showEstimatedGas() {
                return this.item.activity_status === 'pending'
                    && this.item.estimated_gas_eth != null
                    && this.item.estimated_gas_eth !== '';
            },

            addressLabel() {
                if (this.item.purpose === PURPOSE_TYPES.STAKE || this.item.purpose === PURPOSE_TYPES.UNSTAKE) {
                    return null;
                }
                if (this.item.activity_type === ACTIVITY_TYPES.RECEIVE) {
                    return this.$t('userWallet.history.fromAddress');
                }
                if (this.item.activity_type === ACTIVITY_TYPES.SEND) {
                    return this.$t('userWallet.history.toAddress');
                }
                return null;
            },

            addressValue() {
                return this.item.activity_type === ACTIVITY_TYPES.RECEIVE ? this.item.from_address : this.item.to_address;
            },

            explorerBlockUrl() {
                if (!config.ethExplorerUrl || !this.item.block_number) {
                    return null;
                }
                return `${config.ethExplorerUrl.replace(/\/$/, '')}/block/${this.item.block_number}`;
            },

            explorerTxUrl() {
                if (!config.ethExplorerUrl || !this.item.transaction_hash) {
                    return null;
                }
                return `${config.ethExplorerUrl.replace(/\/$/, '')}/tx/${this.item.transaction_hash}`;
            },
        },
    };
</script>
